// Hook para crear una fila nueva en `sesiones` al clickear "Empezar"
// un ejercicio. La sesión nace en `estado='en_progreso'` con
// `started_at = NOW()` (default de la columna no aplica acá porque
// es NOT NULL sin default — lo seteamos explícito).
//
// Devuelve la promesa con el sessionId nuevo (string) o null si falló.
// Patrón coherente con un click handler async: el caller hace `await`,
// si recibe id navega; si recibe null muestra el error que quedó en state.
//
// paciente_id resuelto vía useCurrentPacienteId() (B6). kine_id derivado
// en runtime con un lookup `pacientes.kine_id WHERE id = pacienteId` antes
// del INSERT — atado a la FK del paciente para que la sesión siempre
// quede ligada al kine real del paciente, no a un hardcoded global.
//
// Sub-decisión fail-fast: si el lookup falla o el paciente no existe,
// devolvemos null al caller (HomePaciente muestra el banner de error)
// en lugar de fallback a un kine_id genérico — preferimos no crear la
// sesión a meter una FK rota en DB.

import { useState } from "react";
import { supabase } from "../client";
import { useCurrentPacienteId } from "@/app/components/auth/AuthProvider";

export interface CrearSesionInput {
  planEjercicioId: string;
  ejercicioId: string;
}

export interface UseCrearSesionReturn {
  crear: (input: CrearSesionInput) => Promise<string | null>;
  loading: boolean;
  error: Error | null;
}

export function useCrearSesion(): UseCrearSesionReturn {
  const pacienteId = useCurrentPacienteId();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function crear(input: CrearSesionInput): Promise<string | null> {
    if (!pacienteId) {
      const err = new Error(
        "No pudimos identificar tu cuenta. Iniciá sesión de nuevo.",
      );
      setError(err);
      console.warn("[useCrearSesion] pacienteId null al click");
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      // Lookup del kine del paciente — atamos la sesión al mismo kine
      // que tiene asignado el paciente en FK.
      const { data: pacienteRow, error: lookupError } = await supabase
        .from("pacientes")
        .select("kine_id")
        .eq("id", pacienteId)
        .maybeSingle();
      if (lookupError) throw lookupError;
      if (!pacienteRow?.kine_id) {
        throw new Error(
          "No pudimos identificar a tu kine. Probá de nuevo o contactá soporte.",
        );
      }

      const { data, error: insertError } = await supabase
        .from("sesiones")
        .insert({
          plan_ejercicio_id: input.planEjercicioId,
          paciente_id: pacienteId,
          kine_id: pacienteRow.kine_id as string,
          ejercicio_id: input.ejercicioId,
          started_at: new Date().toISOString(),
          estado: "en_progreso",
        })
        .select("id")
        .single();
      if (insertError) throw insertError;
      if (!data) throw new Error("INSERT no devolvió id");
      return data.id as string;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      console.error("[useCrearSesion] failed:", err);
      return null;
    } finally {
      setLoading(false);
    }
  }

  return { crear, loading, error };
}
